/**
 * Slider image utilities.
 *
 * IMPORTANT — slide1 is always deterministic (no random selection).
 * The LCP element on the landing page is the slide1 image, and
 * public/index.html contains a <link rel="preload"> pointing to the
 * exact slide1 path. If slide1 were random, the preload would point
 * to a different URL than the one React actually loads, wasting the
 * preload and causing a ~2.5s resource load delay.
 *
 * The primary slide1 image is:
 *   /media/landing/slider/slide1/pexels-photo-3184291-min.webp
 * This path must stay in sync with the preload href in index.html.
 *
 * Slides 2–8 remain random — they are below the fold and not preloaded.
 */

// The single fixed image for slide1 — must match index.html preload href
export const SLIDE1_PRIMARY = '/media/landing/slider/slide1/pexels-photo-3184291-min.webp';

// The 480w responsive variant of slide1 — used in srcSet and must match
// the filename convention: replace -min.webp with -480w.webp.
// Single-sourced here so LandingHero.tsx never has to hardcode the path.
export const SLIDE1_PRIMARY_480W = '/media/landing/slider/slide1/pexels-photo-3184291-480w.webp';

const SLIDER_IMAGES: Record<string, string[]> = {
  // slide1 has one entry so the selection is always deterministic.
  // Add alternates here only if you also update the preload hint in index.html.
  slide1: [
    'pexels-photo-3184291-min.webp',
  ],
  slide2: [
    'pexels-photo-302769-min.webp',
    'photo-1449844908441-8829872d2607-min.webp',
    'photo-1486406146926-c627a92ad1ab-min.webp',
  ],
  slide3: [
    'photo-1563986768609-322da13575f3-min.webp',
    'photo-1614064641938-3bbee52942c7-min.webp',
    'security-protection-anti-virus-software-60504-min.webp',
  ],
  slide4: [
    'pexels-photo-373543-min.webp',
    'photo-1451187580459-43490279c0fa-min.webp',
    'photo-1558494949-ef010cbdcc31-min.webp',
  ],
  slide5: [
    'pexels-photo-1181677-min.webp',
    'photo-1526628953301-3e589a6a8b74-min.webp',
  ],
  slide6: [
    'pexels-photo-221148-min.webp',
    'photo-1544551763-46a013bb70d5-min.webp',
    'photo-1602002418816-5c0aeef426aa-min.webp',
  ],
  slide7: [
    'pexels-photo-1516440-min.webp',
    'photo-1556742521-9713bf272865-min.webp',
    'photo-1560518883-ce09059eeffa-min.webp',
  ],
  slide8: [
    'document-agreement-documents-sign-48148-min.webp',
    'photo-1454165804606-c3d57bc86b40-min.webp',
    'photo-1568992687947-868a62a9f521-min.webp',
  ],
};

/**
 * Get an image path for a specific slide.
 * Slide 1 always returns the primary image (for LCP preload consistency).
 * Slides 2–8 return a random image from their pool.
 */
export const getRandomSlideImage = (slideNumber: number): string => {
  const slideKey = `slide${slideNumber}`;
  const images = SLIDER_IMAGES[slideKey];

  if (!images || images.length === 0) return '';

  const index = slideNumber === 1 ? 0 : Math.floor(Math.random() * images.length);
  return `/media/landing/slider/${slideKey}/${images[index]}`;
};